import { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RatingStarsProps {
  rating: number;
  maxStars?: number;
  interactive?: boolean;
  onChange?: (rating: number) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizes = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

export default function RatingStars({
  rating,
  maxStars = 5,
  interactive = false,
  onChange,
  size = 'md',
  className,
}: RatingStarsProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  const displayRating = hovered ?? rating;

  const handleClick = (star: number) => {
    if (interactive && onChange) {
      onChange(star);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, star: number) => {
    if (!interactive || !onChange) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(star);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(star + 1, maxStars);
      onChange(next);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      const prev = Math.max(star - 1, 1);
      onChange(prev);
    }
  };

  return (
    <div
      className={cn('inline-flex items-center gap-0.5', className)}
      role={interactive ? 'radiogroup' : 'img'}
      aria-label={interactive ? 'Star rating' : `${rating} out of ${maxStars} stars`}
    >
      {Array.from({ length: maxStars }, (_, i) => {
        const star = i + 1;
        const filled = star <= Math.round(displayRating);

        const starElement = (
          <Star
            className={cn(
              sizes[size],
              filled ? 'fill-amber-400 text-amber-400' : 'fill-none text-line-strong'
            )}
            aria-hidden="true"
          />
        );

        if (interactive) {
          return (
            <button
              key={star}
              type="button"
              onClick={() => handleClick(star)}
              onKeyDown={(e) => handleKeyDown(e, star)}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(null)}
              className="cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
              role="radio"
              aria-checked={star === Math.round(rating)}
              aria-label={`${star} star${star !== 1 ? 's' : ''}`}
            >
              {starElement}
            </button>
          );
        }

        return <span key={star}>{starElement}</span>;
      })}
    </div>
  );
}
